import type { LucideIcon } from 'lucide-react';
import { Clock, KeyRound, Lock, LogIn, MailCheck, ShieldAlert, ShieldX, Timer } from 'lucide-react';
import type { DenialKind } from './apiError';

/**
 * O texto que o usuário lê quando o backend recusa alguma coisa.
 *
 * Separado do componente de propósito: a `AccessDenied` fica puramente visual
 * (§45) e as ações viajam como **dados** (`{ label, to }`), não como handlers —
 * é o que permite o mesmo texto servir a uma tela inteira e a um toast disparado
 * de dentro do interceptor do axios, que não tem router.
 *
 * Todas as negativas usam a família `warn` (laranja), não `danger`. O design
 * system reserva vermelho para erro e ação destrutiva (§7, §54); "você não tem
 * acesso a isto" é atenção, não falha — e uma cor só para toda a categoria
 * mantém o significado estável entre módulos (§25).
 */

export interface DenialAction {
  label: string;
  /** Rota de destino. Navegação fica com quem renderiza. */
  to: string;
}

export interface DenialCopy {
  title: string;
  description: string;
  icon: LucideIcon;
  primary?: DenialAction;
  secondary?: DenialAction;
  /**
   * Exibir a mensagem crua da API como linha de diagnóstico.
   *
   * Só onde o backend escreve algo que o usuário ganha em ler — no `forbidden`
   * genérico a mensagem dele *é* a explicação. Nos demais casos o texto da API
   * é técnico ou está em inglês, e §31 manda não jogar isso na cara de ninguém.
   */
  showDetail?: boolean;
}

export const denialCopy: Record<DenialKind, DenialCopy> = {
  unauthenticated: {
    title: 'Você precisa entrar',
    description: 'Faça login para continuar de onde parou.',
    icon: LogIn,
    primary: { label: 'Entrar', to: '/login' },
  },

  'session-expired': {
    title: 'Sua sessão expirou',
    description:
      'Isso acontece quando você entra em outro dispositivo ou fica muito tempo sem usar o site.',
    icon: Clock,
    primary: { label: 'Entrar novamente', to: '/login' },
  },

  subscription: {
    title: 'Sua assinatura não está ativa',
    description: 'Renove seu plano para voltar a praticar com o banco de questões.',
    icon: Lock,
    primary: { label: 'Ver planos', to: '/planos' },
    secondary: { label: 'Voltar ao início', to: '/home' },
    showDetail: true,
  },

  'admin-only': {
    title: 'Área restrita',
    description:
      'Esta seção é exclusiva de administradores. Se você precisa deste acesso, fale com o suporte.',
    icon: ShieldAlert,
    primary: { label: 'Voltar ao início', to: '/home' },
    secondary: { label: 'Falar com o suporte', to: '/contato' },
  },

  ownership: {
    title: 'Este conteúdo não é seu',
    description: 'Você só pode alterar cartões e respostas que você mesmo criou.',
    icon: ShieldX,
    primary: { label: 'Voltar ao início', to: '/home' },
    showDetail: true,
  },

  forbidden: {
    title: 'Ação não permitida',
    description: 'O sistema recusou esta ação.',
    icon: ShieldAlert,
    primary: { label: 'Voltar ao início', to: '/home' },
    showDetail: true,
  },

  'email-not-verified': {
    title: 'Confirme seu e-mail',
    description:
      'Enviamos um link de confirmação para o seu e-mail. Abra-o para liberar o acesso.',
    icon: MailCheck,
    primary: { label: 'Voltar ao login', to: '/login' },
  },

  'readonly-key': {
    title: 'Credencial somente leitura',
    description: 'Esta chave de integração pode consultar dados, mas não alterá-los.',
    icon: KeyRound,
  },

  'rate-limited': {
    title: 'Muitas ações em pouco tempo',
    description: 'Aguarde alguns segundos e tente de novo.',
    icon: Timer,
  },
};

/** Ruído de validação do Gin (`ShouldBindJSON`), que nunca deve chegar ao usuário. */
const GIN_BINDING_NOISE = /^Key: '|Error:Field validation/;

/**
 * A mensagem da API vale a pena ser mostrada?
 *
 * Filtra o vazio, o ruído de binding do Gin e textos longos demais para uma
 * linha de apoio.
 */
export function denialDetail(kind: DenialKind, raw: string): string | undefined {
  if (!denialCopy[kind].showDetail) return undefined;

  const text = raw.trim();
  if (!text || text.length > 160 || GIN_BINDING_NOISE.test(text)) return undefined;

  return text;
}
